import type { AuditFieldDef, InferAuditData } from "./fields";

/**
 * Catálogo da auditoria (tabela `AuditLogs`). ÚNICA fonte do que pode ser
 * gravado: ação, estrutura do `meta.data`, origem e resultado.
 *
 * Valores vão para o banco como texto e a tela traduz por eles — mudar um
 * valor já gravado deixa o histórico sem tradução. Novo tipo = nova linha
 * aqui, sem migration.
 */

type ValuesOf<T> = T[keyof T];

// ── Ações ────────────────────────────────────────────────────────────────

/** Chave da ação, formato `<entidade>.<verbo>`. */
export const AUDIT_ACTIONS = {
  CONNECTION: {
    /** Conexão excluída (tela de conexões ou tela de admin). */
    DELETED: "connection.deleted",
    /** Tickets abertos/pendentes/de grupo movidos para outra conexão. entityId = origem. */
    TICKETS_TRANSFERRED: "connection.tickets_transferred"
  },
  AUTH: {
    /**
     * Credencial aceita em POST /auth/login. entityId = id do usuário.
     *
     * Fluxo SEPARADO do de presença (`SESSION.*`): logar e estar presente são
     * fatos diferentes. Quem entra grava os dois — `auth.logged_in` na
     * credencial e `session.started` quando o socket conecta —, e quem só
     * reabre uma aba grava apenas presença. Decidido assim porque a #4454
     * voltou com o pedido de separar "entrada/saída" de "login/logoff".
     */
    LOGGED_IN: "auth.logged_in",
    /** Clique em Sair (DELETE /auth/logout). entityId = id do usuário. */
    LOGGED_OUT: "auth.logged_out"
  },
  SESSION: {
    /**
     * Atendente abriu o Markedesk (primeira aba; abrir a segunda não repete).
     * entityId = id do usuário.
     */
    STARTED: "session.started",
    /** Atendente fechou a última aba, ou a conexão caiu. */
    ENDED: "session.ended"
  }
} as const;

export type AuditAction = ValuesOf<{ [K in keyof typeof AUDIT_ACTIONS]: ValuesOf<(typeof AUDIT_ACTIONS)[K]> }>;

// ── Origem e resultado (colunas) ─────────────────────────────────────────

/** De onde a ação veio. Com `userId` e `tokenId` nulos, é o que diz quem fez. */
export const AUDIT_SOURCES = {
  /** Usuário logado no painel (JWT). */
  WEB: "web",
  /** API pública pelo token de conexão. */
  API: "api",
  /** Plugin chamando o backend. */
  PLUGIN: "plugin",
  /** Job em background (BullMQ, cron). */
  JOB: "job",
  /** O próprio sistema, fora de request. */
  SYSTEM: "system"
} as const;

export type AuditSource = ValuesOf<typeof AUDIT_SOURCES>;

export const AUDIT_STATUSES = {
  /** A ação aconteceu. */
  SUCCESS: "success",
  /** Recusada por permissão. */
  DENIED: "denied",
  /** Tentada e falhou no meio. */
  FAILED: "failed"
} as const;

export type AuditStatus = ValuesOf<typeof AUDIT_STATUSES>;

// ── Estruturas do `meta.data` ────────────────────────────────────────────

export interface AuditStructDef {
  /**
   * Versão do formato. Numa versão já gravada, campo só pode ser ACRESCENTADO
   * como `optional` — remover, renomear ou mudar tipo exige versão nova, senão
   * o histórico antigo deixa de ser lido. O snapshot em
   * `__tests__/auditCatalog.test.ts` é o que trava isso.
   */
  version: number;
  fields: Readonly<Record<string, AuditFieldDef>>;
}

const defineStructs = <T extends Record<string, AuditStructDef>>(structs: T): T => structs;

/**
 * "Fotos" de entidade: o que o registro precisa guardar para continuar
 * legível depois que a entidade mudar ou sumir.
 *
 * Nome de campo NÃO pode repetir coluna da tabela (ver AUDIT_COLUMNS): o que é
 * coluna não vai para o `meta`.
 */
export const AUDIT_STRUCTS = defineStructs({
  connection: {
    version: 1,
    fields: {
      name: { kind: "text", max: 120, role: "title", filterable: true },
      channel: { kind: "text", max: 40, role: "subtitle", filterable: true },
      number: { kind: "text", max: 30, optional: true, pii: true }
    }
  },
  /**
   * Sessão do atendente (#4433) e entrada/saída por credencial (`AUTH.*`).
   * Guarda o NOME para o registro continuar legível depois que o usuário for
   * excluído — `userId` é coluna e não tem FK. Sem e-mail: o nome identifica o
   * registro e a auditoria não precisa guardar mais dado pessoal que isso.
   */
  session: {
    version: 1,
    fields: {
      name: { kind: "text", max: 120, role: "title", filterable: true }
    }
  },
  ticketTransfer: {
    version: 1,
    fields: {
      fromName: { kind: "text", max: 120, role: "title", filterable: true },
      toName: { kind: "text", max: 120, role: "subtitle", filterable: true },
      toConnectionId: { kind: "number" },
      /** Quantos foram movidos de fato (em `failed`, o parcial). */
      ticketCount: { kind: "number" }
    }
  }
} as const);

export type AuditStructName = keyof typeof AUDIT_STRUCTS;

// ── Ação → entidade e estrutura ──────────────────────────────────────────

export interface AuditActionDef {
  /** Entidade afetada — `entityId` do registro é dela. */
  entity: string;
  /** Estrutura do `meta.data`; null = ação sem dados próprios. */
  struct: AuditStructName | null;
}

const defineActions = <T extends Record<AuditAction, AuditActionDef>>(actions: T): T => actions;

export const AUDIT_ACTION_DEFS = defineActions({
  [AUDIT_ACTIONS.CONNECTION.DELETED]: { entity: "connection", struct: "connection" },
  [AUDIT_ACTIONS.CONNECTION.TICKETS_TRANSFERRED]: { entity: "connection", struct: "ticketTransfer" },
  [AUDIT_ACTIONS.AUTH.LOGGED_IN]: { entity: "auth", struct: "session" },
  [AUDIT_ACTIONS.AUTH.LOGGED_OUT]: { entity: "auth", struct: "session" },
  [AUDIT_ACTIONS.SESSION.STARTED]: { entity: "session", struct: "session" },
  [AUDIT_ACTIONS.SESSION.ENDED]: { entity: "session", struct: "session" }
} as const);

type DefOf<A extends AuditAction> = (typeof AUDIT_ACTION_DEFS)[A];

/** Tipo do `meta.data` de uma ação, derivado do catálogo. */
export type AuditDataOf<A extends AuditAction> = DefOf<A>["struct"] extends AuditStructName
  ? InferAuditData<(typeof AUDIT_STRUCTS)[DefOf<A>["struct"]]["fields"]>
  : null;

/**
 * O que quem grava pode entregar para a foto: qualquer objeto com (alguns
 * dos) campos da estrutura — inclusive a própria instância do model. Só os
 * campos do catálogo são lidos.
 */
export type AuditSubjectOf<A extends AuditAction> = DefOf<A>["struct"] extends AuditStructName
  ? { [K in keyof (typeof AUDIT_STRUCTS)[DefOf<A>["struct"]]["fields"]]?: unknown }
  : null;

export type AuditEntity = DefOf<AuditAction>["entity"];

/**
 * Colunas da tabela. O que está aqui NUNCA entra no `meta` — é a regra que
 * impede redundância entre coluna e JSON.
 */
export const AUDIT_COLUMNS = [
  "id",
  "companyId",
  "userId",
  "tokenId",
  "source",
  "support",
  "action",
  "status",
  "entityId",
  "meta",
  "createdAt"
] as const;
